<template>
    <v-container fluid class="pa-4">
        <div class="d-flex align-center mb-4">
            <v-btn icon variant="text" size="small" class="mr-2" @click="$router.push('/business-dashboard')">
                <v-icon>mdi-arrow-left</v-icon>
            </v-btn>
            <v-icon color="teal" size="28" class="mr-3">mdi-autorenew</v-icon>
            <div>
                <h1 class="text-h5 font-weight-bold">Renewal Dashboard</h1>
                <p class="text-caption text-medium-emphasis mb-0">
                    Monthly renewal status per portal, and the controls for the automated run
                </p>
            </div>
            <v-spacer/>
            <v-select v-model="period" :items="periodOptions" item-title="label" item-value="value"
                      density="compact" variant="outlined" hide-details style="max-width: 180px" class="mr-2"/>
            <v-btn variant="text" :loading="loading" prepend-icon="mdi-refresh" @click="loadAll">Refresh</v-btn>
        </div>

        <v-overlay :model-value="loading && !settings" contained class="align-center justify-center">
            <v-progress-circular indeterminate size="48"/>
        </v-overlay>

        <v-alert v-if="error" type="error" variant="tonal" closable class="mb-4" @click:close="error = null">
            {{ error }}
            <template #append>
                <v-btn variant="text" size="small" @click="loadAll">Retry</v-btn>
            </template>
        </v-alert>

        <!-- Controls. Deliberately first: this is the page's real power. -->
        <v-card elevation="2" class="mb-4">
            <v-card-title class="d-flex align-center py-3">
                <v-icon color="teal" size="20" class="mr-2">mdi-tune-variant</v-icon>
                Automated Run
                <v-spacer/>
                <!-- Never claims DISABLED before the server has said so. Grey and
                     confident is exactly how "we could not reach the API" would
                     read as "the run is off, nothing is being charged". -->
                <v-chip size="small" :color="runStateColor" variant="flat">{{ runStateLabel }}</v-chip>
            </v-card-title>
            <v-divider/>
            <v-card-text v-if="settings">
                <v-row dense>
                    <v-col cols="12" md="6">
                        <div class="d-flex align-start">
                            <v-switch v-model="runEnabledUi" color="green"
                                      hide-details density="compact" class="mr-4 flex-grow-0"
                                      :loading="savingKey === 'billing_run_enabled'"
                                      :disabled="savingKey === 'billing_run_enabled'"
                                      @update:model-value="v => askSwitch('billing_run_enabled', v)"/>
                            <div>
                                <div class="font-weight-medium">
                                    Master switch
                                    <v-chip size="x-small" variant="tonal" class="ml-1">
                                        {{ settings.billing_run_enabled.source }}
                                    </v-chip>
                                </div>
                                <div class="text-caption text-medium-emphasis">
                                    {{ settings.billing_run_enabled.description }}
                                </div>
                            </div>
                        </div>
                    </v-col>
                    <v-col cols="12" md="6">
                        <div class="d-flex align-start">
                            <v-switch v-model="autoPayUi" color="green"
                                      hide-details density="compact" class="mr-4 flex-grow-0"
                                      :loading="savingKey === 'billing_auto_pay'"
                                      :disabled="savingKey === 'billing_auto_pay'"
                                      @update:model-value="v => askSwitch('billing_auto_pay', v)"/>
                            <div>
                                <div class="font-weight-medium">
                                    Charge the card automatically
                                    <v-chip size="x-small" variant="tonal" class="ml-1">
                                        {{ settings.billing_auto_pay.source }}
                                    </v-chip>
                                </div>
                                <div class="text-caption text-medium-emphasis">
                                    {{ settings.billing_auto_pay.description }}
                                </div>
                            </div>
                        </div>
                    </v-col>
                </v-row>

                <v-divider class="my-4"/>

                <v-row dense>
                    <v-col cols="12" md="6">
                        <div class="d-flex align-start">
                            <v-switch v-model="accountRenewalUi" color="green"
                                      hide-details density="compact" class="mr-4 flex-grow-0"
                                      :loading="savingKey === 'account_renewal_enabled'"
                                      :disabled="savingKey === 'account_renewal_enabled'"
                                      @update:model-value="v => askSwitch('account_renewal_enabled', v)"/>
                            <div>
                                <div class="font-weight-medium">
                                    Go Family renewal (daily)
                                    <v-chip size="x-small" variant="tonal" class="ml-1">
                                        {{ settings.account_renewal_enabled.source }}
                                    </v-chip>
                                </div>
                                <div class="text-caption text-medium-emphasis">
                                    {{ settings.account_renewal_enabled.description }}
                                </div>
                            </div>
                        </div>
                    </v-col>
                    <v-col cols="12" md="6" class="text-caption text-medium-emphasis">
                        <div>
                            <v-icon size="14" class="mr-1">mdi-history</v-icon>
                            Renewal last ran:
                            <strong>{{ settings.account_renewal_last_run.ticked_at || 'never' }}</strong>
                        </div>
                        <div v-if="settings.account_renewal_last_run.result" class="ml-5">
                            {{ settings.account_renewal_last_run.result }}
                        </div>
                        <div class="ml-5">{{ settings.account_renewal_last_run.description }}</div>
                    </v-col>
                </v-row>

                <v-divider class="my-4"/>

                <v-row dense align="center">
                    <v-col cols="12" md="8">
                        <v-combobox v-model="emails" multiple chips closable-chips
                                    density="compact" variant="outlined" hide-details
                                    label="Notification recipients"
                                    :hint="settings.billing_notification_emails.description"/>
                    </v-col>
                    <v-col cols="12" md="4" class="d-flex align-center">
                        <v-btn color="primary" variant="tonal" :loading="savingKey === 'billing_notification_emails'"
                               :disabled="!emailsChanged || !emails.length" @click="saveEmails">
                            Save recipients
                        </v-btn>
                        <v-chip size="x-small" variant="tonal" class="ml-2">
                            {{ settings.billing_notification_emails.source }}
                        </v-chip>
                        <span v-if="!emails.length" class="text-caption text-medium-emphasis ml-2">
                            At least one recipient is required
                        </span>
                    </v-col>
                </v-row>

                <div class="text-caption text-medium-emphasis mt-3">
                    <v-icon size="14" class="mr-1">mdi-calendar-clock</v-icon>
                    Runs on day {{ settings.schedule.day_of_month }} of each month at
                    {{ settings.schedule.time }} {{ settings.schedule.timezone }}.
                    {{ settings.schedule.description }}
                </div>
            </v-card-text>
        </v-card>

        <!-- Totals. These are as-of-now, not for the selected period: they answer
             "what would the run charge if it ran right now". -->
        <template v-if="data">
            <div class="d-flex align-center mb-2">
                <v-icon size="16" class="mr-1">mdi-clock-outline</v-icon>
                <span class="text-caption text-medium-emphasis">
                    Position as of {{ formatDate(data.generated_at) }} - the period selector applies to the
                    invoice columns only
                </span>
            </div>
            <v-row dense class="mb-4">
                <v-col cols="6" md="3">
                    <v-card elevation="2" class="pa-4">
                        <div class="text-caption text-medium-emphasis">Due on the next run</div>
                        <div class="text-h4 font-weight-bold">{{ data.totals.due_now }}</div>
                        <div class="text-caption">of {{ data.totals.subscribed }} subscribed devices</div>
                    </v-card>
                </v-col>
                <v-col cols="6" md="3">
                    <v-card elevation="2" class="pa-4">
                        <div class="text-caption text-medium-emphasis">Projected charge</div>
                        <div class="text-h5 font-weight-bold text-green">{{ money(data.totals.projected) }}</div>
                        <div class="text-caption">
                            if the run happened now<template v-if="data.totals.discounted">,
                            {{ data.totals.discounted }} at a promotional price</template>
                        </div>
                    </v-card>
                </v-col>
                <v-col cols="6" md="3">
                    <v-card elevation="2" class="pa-4">
                        <div class="text-caption text-medium-emphasis">Already invoiced</div>
                        <div class="text-h4 font-weight-bold text-orange">{{ data.totals.awaiting_payment }}</div>
                        <div class="text-caption">billed this month, so not billed again</div>
                    </v-card>
                </v-col>
                <v-col cols="6" md="3">
                    <v-card elevation="2" class="pa-4">
                        <div class="text-caption text-medium-emphasis">Blocked</div>
                        <div class="text-h4 font-weight-bold" :class="data.totals.blocked ? 'text-red' : ''">
                            {{ data.totals.blocked }}
                        </div>
                        <div class="text-caption">
                            devices the run will not bill, in {{ data.totals.needs_attention }} issue(s)
                        </div>
                    </v-card>
                </v-col>
            </v-row>
        </template>

        <!-- Per portal -->
        <v-card elevation="2" class="mb-4" v-if="data">
            <v-card-title class="py-3">
                <v-icon color="blue" size="20" class="mr-2">mdi-domain</v-icon>
                By Portal
            </v-card-title>
            <v-divider/>
            <v-card-text class="pa-0">
                <div style="overflow-x: auto">
                    <v-table density="compact">
                        <thead>
                            <tr>
                                <th>Portal</th>
                                <th class="text-right">Companies</th>
                                <th class="text-right">Subscribed</th>
                                <th class="text-right">Due now</th>
                                <th class="text-right">Already invoiced</th>
                                <th class="text-right">Discounted</th>
                                <th class="text-right">Blocked</th>
                                <th class="text-right">Projected</th>
                                <th class="text-right">Collected ({{ data.period }})</th>
                                <th class="text-right">Outstanding ({{ data.period }})</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="row in data.portals" :key="row.portal_id">
                                <td>
                                    <div class="font-weight-medium">{{ row.portal_name }}</div>
                                    <div class="text-caption text-medium-emphasis" v-if="row.portal_type">
                                        {{ row.portal_type }}
                                    </div>
                                    <div class="text-caption text-medium-emphasis" v-if="row.note">{{ row.note }}</div>
                                </td>
                                <td class="text-right">
                                    {{ row.companies.billable }} / {{ row.companies.total }}
                                    <v-tooltip activator="parent" location="top">
                                        {{ row.companies.suspended }} suspended, {{ row.companies.exempt }} exempt
                                    </v-tooltip>
                                </td>
                                <td class="text-right">{{ row.devices.subscribed }}</td>
                                <td class="text-right font-weight-bold">{{ row.devices.due_now }}</td>
                                <td class="text-right">{{ row.devices.awaiting_payment }}</td>
                                <td class="text-right">{{ row.devices.discounted }}</td>
                                <td class="text-right">
                                    <span :class="row.devices.blocked ? 'text-red font-weight-bold' : ''">
                                        {{ row.devices.blocked }}
                                    </span>
                                </td>
                                <td class="text-right text-green">{{ money(row.money.projected) }}</td>
                                <td class="text-right">{{ money(row.money.collected) }}</td>
                                <td class="text-right text-orange">{{ money(row.money.outstanding) }}</td>
                            </tr>
                            <tr v-if="!data.portals.length">
                                <td colspan="10" class="text-center text-medium-emphasis py-4">No portals</td>
                            </tr>
                        </tbody>
                    </v-table>
                </div>
                <div class="text-caption text-medium-emphasis pa-3">
                    Projected is the next run only. Collected and outstanding count every invoice dated in the
                    selected period, activations included.
                </div>
            </v-card-text>
        </v-card>

        <!-- Devices the run will not bill -->
        <v-card elevation="2" class="mb-4" v-if="data && data.totals.blocked">
            <v-card-title class="py-3">
                <v-icon color="red" size="20" class="mr-2">mdi-alert-circle</v-icon>
                Will Not Be Billed
                <v-spacer/>
                <v-chip size="small" color="red" variant="tonal">{{ data.totals.blocked }} device(s)</v-chip>
            </v-card-title>
            <v-divider/>
            <v-card-text class="pa-0">
                <v-table density="compact">
                    <thead>
                        <tr>
                            <th>Portal</th>
                            <th>Company</th>
                            <th>Device</th>
                            <th>Reason</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-if="!attentionRows.length">
                            <td colspan="4" class="text-center text-medium-emphasis py-4">
                                No per-device detail was returned for these devices.
                            </td>
                        </tr>
                        <tr v-for="(row, i) in attentionRows" :key="i">
                            <td class="text-medium-emphasis">{{ row.portal }}</td>
                            <td>{{ row.company }}</td>
                            <td>{{ row.device }}</td>
                            <td>{{ row.reason }}</td>
                        </tr>
                    </tbody>
                </v-table>
                <div v-if="attentionTruncated" class="text-caption text-medium-emphasis pa-3">
                    Showing the first 50 issues per portal. {{ data.totals.needs_attention }} in total.
                </div>
            </v-card-text>
        </v-card>

        <!-- Attempt log -->
        <v-card elevation="2">
            <v-card-title class="d-flex align-center py-3 flex-wrap">
                <v-icon color="indigo" size="20" class="mr-2">mdi-format-list-checks</v-icon>
                Renewal Attempts
                <span class="text-caption text-medium-emphasis ml-2">{{ period }}</span>
                <v-spacer/>
                <v-chip v-for="(count, status) in summary?.by_status || {}" :key="status"
                        size="small" class="ml-1" variant="tonal" :color="statusColor(status)"
                        @click="filterStatus = filterStatus === status ? null : status">
                    {{ status }}: {{ count }}
                </v-chip>
            </v-card-title>
            <v-divider/>
            <v-card-text>
                <v-row dense class="mb-2">
                    <v-col cols="12" md="3">
                        <v-select v-model="filterStatus" :items="statusOptions" clearable label="Status"
                                  density="compact" variant="outlined" hide-details/>
                    </v-col>
                    <v-col cols="12" md="3">
                        <v-select v-model="filterFailureCode" :items="failureCodeOptions" clearable
                                  label="Failure code" density="compact" variant="outlined" hide-details/>
                    </v-col>
                    <v-col cols="12" md="3">
                        <v-select v-model="filterPortal" :items="portalOptions" item-title="label" item-value="value"
                                  clearable label="Portal" density="compact" variant="outlined" hide-details/>
                    </v-col>
                </v-row>

                <div style="overflow-x: auto" :class="attemptsLoading ? 'op-half' : ''">
                    <v-table density="compact">
                        <thead>
                            <tr>
                                <th>When</th>
                                <th>Company</th>
                                <th>Device</th>
                                <th>Status</th>
                                <th>Failure code</th>
                                <th class="text-right">Amount</th>
                                <th>Notes</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="row in attempts" :key="row.id">
                                <td class="text-no-wrap">{{ formatDate(row.attempted_at) }}</td>
                                <td>{{ row.company?.company_name || '-' }}</td>
                                <td>{{ row.device?.slno || '(company level)' }}</td>
                                <td>
                                    <v-chip size="x-small" variant="flat" :color="statusColor(row.status)">
                                        {{ row.status }}
                                    </v-chip>
                                </td>
                                <td>{{ row.failure_code || '-' }}</td>
                                <td class="text-right">{{ amount(row) }}</td>
                                <td class="text-caption">{{ row.notes }}</td>
                            </tr>
                            <tr v-if="!attempts.length">
                                <td colspan="7" class="text-center text-medium-emphasis py-4">
                                    <template v-if="error">Attempts could not be loaded.</template>
                                    <template v-else-if="attemptsLoading">Loading...</template>
                                    <template v-else>
                                        No attempts recorded for {{ period }}.
                                        <template v-if="settings && !settings.billing_run_enabled.value">
                                            The automated run is disabled.
                                        </template>
                                    </template>
                                </td>
                            </tr>
                        </tbody>
                    </v-table>
                </div>

                <v-pagination v-if="lastPage > 1" v-model="page" :length="lastPage" density="compact"
                              total-visible="7" class="mt-3"/>
            </v-card-text>
        </v-card>

        <!-- Turning either switch ON leads to money moving without anyone
             watching, so both ask. Turning them OFF is safe and does not. -->
        <!-- Not `persistent`: the axios client has no timeout, so a stalled PUT
             leaves savingKey set forever, and a dialog that swallows Esc and
             click-outside with both buttons inert would strand the page. -->
        <v-dialog v-model="confirmOpen" max-width="540" @update:model-value="v => v || cancelConfirm()">
            <v-card v-if="confirmKey">
                <v-card-title class="text-h6">{{ confirmCopy.title }}</v-card-title>
                <v-card-text>
                    <div>{{ confirmCopy.body }}</div>
                    <div class="mt-3" v-if="data">
                        Right now that would be
                        <strong>{{ data.totals.due_now }} device(s)</strong> for
                        <strong>{{ money(data.totals.projected) }}</strong>,
                        <span v-if="data.totals.discounted">
                            {{ data.totals.discounted }} of them in a promotional month,
                        </span>
                        as of {{ formatDate(data.generated_at) }}.
                        <v-btn variant="text" size="x-small" :loading="refreshingImpact"
                               @click="refreshImpact">Recheck</v-btn>
                    </div>
                    <v-alert v-else type="warning" variant="tonal" density="compact" class="mt-3">
                        The current position could not be loaded, so the impact of this change is unknown.
                    </v-alert>
                </v-card-text>
                <v-card-actions>
                    <v-spacer/>
                    <v-btn variant="text" :disabled="savingKey === confirmKey" @click="cancelConfirm">Cancel</v-btn>
                    <v-btn color="green" variant="flat" :loading="savingKey === confirmKey" @click="acceptConfirm">
                        {{ confirmCopy.action }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-snackbar v-model="toast" :color="toastColor" timeout="4000">{{ toastText }}</v-snackbar>
    </v-container>
</template>

<script setup>
import {ref, computed, onMounted, watch} from 'vue';
import {send} from '@/composables/apiClient';

const loading = ref(false);
const attemptsLoading = ref(false);
const error = ref(null);
const data = ref(null);
const settings = ref(null);
const summary = ref(null);
const attempts = ref([]);
const page = ref(1);
const lastPage = ref(1);
// One in flight at a time. Two saves racing would let the loser's response
// overwrite the winner's state, and both clear the same key in their finally.
const savingKey = ref(null);
const refreshingImpact = ref(false);
const emails = ref([]);
const toast = ref(false);
const toastText = ref('');
const toastColor = ref('success');

const filterStatus = ref(null);
const filterFailureCode = ref(null);
const filterPortal = ref(null);

// The switches are bound to LOCAL state, not straight to `settings`. Vuetify's
// v-switch only re-patches the DOM input when the bound value CHANGES, so
// binding it to a value that never changed - cancelling the dialog, or a failed
// save that restores the same boolean - leaves the visible switch flipped and
// the model not, and the next click emits nothing at all. These refs are the
// server's answer, and every path that declines a change writes to them.
const runEnabledUi = ref(false);
const autoPayUi = ref(false);
const accountRenewalUi = ref(false);

const confirmKey = ref(null);
const confirmOpen = ref(false);

const statusOptions = ['paid', 'unpaid', 'skipped', 'error'];
const failureCodeOptions = [
    'no_card', 'card_declined', 'no_start_date', 'unreadable_date',
    'active_discount', 'mixed_currency', 'autopay_off', 'run_error',
    'charged_not_recorded', 'not_extended',
];

// The last twelve months, newest first. The API keys everything on 'YYYY-MM'.
const periodOptions = (() => {
    const out = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        out.push({
            label: d.toLocaleDateString('en-US', {month: 'long', year: 'numeric'}),
            value: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
        });
    }
    return out;
})();
const period = ref(periodOptions[0].value);

const runStateLabel = computed(() => {
    if (!settings.value) return 'UNKNOWN';
    return settings.value.billing_run_enabled.value ? 'ENABLED' : 'DISABLED';
});
const runStateColor = computed(() => {
    if (!settings.value) return 'grey-lighten-1';
    return settings.value.billing_run_enabled.value ? 'green' : 'blue-grey';
});

const confirmCopy = computed(() => {
    if (confirmKey.value === 'billing_run_enabled') {
        return {
            title: 'Enable automated billing?',
            body: autoPayUi.value
                ? 'From the next scheduled run, invoices will be raised and the saved card charged for every device that is due, with nobody watching.'
                : 'From the next scheduled run, invoices will be raised for every device that is due and left payable, with nobody watching.',
            action: 'Enable',
        };
    }
    if (confirmKey.value === 'account_renewal_enabled') {
        return {
            title: 'Stop renewing Go Family plans?',
            body: 'No card will be charged and no subscription will advance. There is no grace '
                + 'period to fall back on - grace only opens when a charge fails, and none will be '
                + 'attempted - so every Family subscriber DROPS TO THE FREE TIER on their own '
                + 'period-end date, starting today. Use this to stop a misbehaving run, then turn '
                + 'it back on and catch up with --force.',
            action: 'Stop renewals',
        };
    }
    return {
        title: 'Charge cards automatically?',
        body: settings.value?.billing_run_enabled?.value
            ? 'Each invoice the run raises will be charged to the customer\'s saved card immediately, with nobody watching. The run is currently enabled, so this takes effect on the next scheduled run.'
            : 'Each invoice the run raises will be charged to the customer\'s saved card immediately. The master switch is off, so nothing happens until it is turned on.',
        action: 'Charge automatically',
    };
});

// Portal 0 is the "No live portal" row and it is selectable like any other, so
// this list must not filter on truthiness. Same reason the API stopped using
// ->when($request->input('portal_id')).
const portalOptions = computed(() =>
    (data.value?.portals || []).map(p => ({label: p.portal_name, value: p.portal_id}))
);

const emailsChanged = computed(() => {
    const current = settings.value?.billing_notification_emails?.value || [];
    return JSON.stringify([...current].sort()) !== JSON.stringify([...emails.value].sort());
});

// Flattened so an operator sees every unbillable device in one list rather than
// having to open each portal.
const attentionRows = computed(() =>
    (data.value?.portals || []).flatMap(p =>
        (p.attention || []).map(a => ({...a, portal: p.portal_name}))
    )
);
const attentionTruncated = computed(() =>
    (data.value?.portals || []).some(p => p.attention_truncated)
);

function money(byCurrency) {
    const entries = Object.entries(byCurrency || {});
    if (!entries.length) return '0.00';
    return entries
        .map(([currency, value]) => `${currency} ${Number(value).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`)
        .join(' + ');
}

function amount(row) {
    if (row.amount === null || row.amount === undefined) return '-';
    return money({[(row.currency || 'USD').toUpperCase()]: row.amount});
}

function statusColor(status) {
    return {paid: 'green', unpaid: 'orange', skipped: 'blue-grey', error: 'red'}[status] || 'grey';
}

function formatDate(value) {
    if (!value) return '-';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return value;
    return parsed.toLocaleString('en-US', {
        year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
}

function notify(text, color = 'success') {
    toastText.value = text;
    toastColor.value = color;
    toast.value = true;
}

function periodParts() {
    const [year, month] = period.value.split('-');
    return {year: Number(year), month: Number(month)};
}

function syncSwitches() {
    runEnabledUi.value = !!settings.value?.billing_run_enabled?.value;
    autoPayUi.value = !!settings.value?.billing_auto_pay?.value;
    accountRenewalUi.value = !!settings.value?.account_renewal_enabled?.value;
}

// Read everything off the payload BEFORE committing it. A reshaped response
// that throws halfway would otherwise leave `settings` assigned but the
// switches unsynced, report a successful save as a failure, and hand the
// template a half-applied object to dereference.
function applySettings(payload, {keepEmailEdits = true} = {}) {
    const recipients = payload?.billing_notification_emails?.value;
    const nextEmails = Array.isArray(recipients)
        ? [...recipients]
        : String(recipients || '').split(',').map(s => s.trim()).filter(Boolean);
    const runEnabled = !!payload?.billing_run_enabled?.value;
    const autoPay = !!payload?.billing_auto_pay?.value;
    const accountRenewal = !!payload?.account_renewal_enabled?.value;
    // Only overwrite the recipient box when the user is not mid-edit, so a
    // refresh or an unrelated save does not silently discard typed addresses.
    const dirty = keepEmailEdits && emailsChanged.value;

    settings.value = payload;
    runEnabledUi.value = runEnabled;
    autoPayUi.value = autoPay;
    accountRenewalUi.value = accountRenewal;
    if (!dirty) {
        emails.value = nextEmails;
    }
}

async function loadDashboard() {
    const response = await send('get', 'admin/renewals', periodParts());
    data.value = response.data.data;
}

async function loadSettings(options) {
    const response = await send('get', 'admin/renewals/settings');
    applySettings(response.data.data, options);
}

// Every attempts request carries a sequence number and a late one is discarded.
// Filters are clicked faster than a paginated query returns, and a stale
// response landing last leaves old rows sitting under new filter chips.
let attemptsSeq = 0;

async function loadAttempts() {
    const seq = ++attemptsSeq;
    attemptsLoading.value = true;
    try {
        const [summaryResponse, listResponse] = await Promise.all([
            send('get', 'admin/renewals/attempt-summary', {period: period.value}),
            send('get', 'admin/renewals/attempts', {
                period: period.value,
                status: filterStatus.value || undefined,
                failure_code: filterFailureCode.value || undefined,
                // ?? not ||: portal 0 is a real selection, not "no filter".
                portal_id: filterPortal.value ?? undefined,
                page: page.value,
            }),
        ]);
        if (seq !== attemptsSeq) return;
        summary.value = summaryResponse.data.data;
        const paginator = listResponse.data.data;
        attempts.value = paginator.data || [];
        lastPage.value = paginator.last_page || paginator.meta?.last_page || 1;
    } finally {
        if (seq === attemptsSeq) attemptsLoading.value = false;
    }
}

async function reloadAttempts() {
    const seq = attemptsSeq + 1;
    try {
        await loadAttempts();
        // Only the newest request may clear the banner, and only its own failure
        // may raise one - a slow failure from the previous filter must not sit
        // over the rows that did arrive.
        if (seq === attemptsSeq) error.value = null;
    } catch (e) {
        if (seq !== attemptsSeq) return;
        error.value = e?.response?.data?.message || 'Could not load the renewal attempts.';
    }
}

async function loadAll() {
    loading.value = true;
    error.value = null;
    try {
        await Promise.all([loadDashboard(), loadSettings(), loadAttempts()]);
    } catch (e) {
        error.value = e?.response?.data?.message || 'Could not load the renewal dashboard.';
    } finally {
        loading.value = false;
    }
}

/**
 * The two BILLING switches make money move unattended, so they confirm on the
 * way ON: turning them off is the safe direction, and confirming there only
 * trains people to click through.
 *
 * The Go Family renewal is the other way round, and deliberately so. It is
 * already on, and turning it OFF is what causes harm: no charge is attempted,
 * so no dunning grace ever opens, so every subscriber drops to the free tier on
 * their own period-end date - starting the day it is flipped. The dangerous
 * direction is the one that gets confirmed, whichever direction that is.
 */
const CONFIRM_WHEN_TURNED_OFF = ['account_renewal_enabled'];

function askSwitch(key, value) {
    const dangerous = CONFIRM_WHEN_TURNED_OFF.includes(key) ? !value : value;

    if (!dangerous) {
        saveSetting(key, value);

        return;
    }
    confirmKey.value = key;
    confirmOpen.value = true;
}

// The dialog quotes what would be charged; it is worth being current, because
// it is the number the decision is made on.
async function refreshImpact() {
    refreshingImpact.value = true;
    try {
        await loadDashboard();
    } catch (e) {
        notify(e?.response?.data?.message || 'Could not refresh the current position.', 'error');
    } finally {
        refreshingImpact.value = false;
    }
}

function cancelConfirm() {
    confirmOpen.value = false;
    confirmKey.value = null;
    // Put the visible switch back where the server has it.
    syncSwitches();
}

async function acceptConfirm() {
    const key = confirmKey.value;
    // The confirmed value is whichever direction was the dangerous one.
    await saveSetting(key, !CONFIRM_WHEN_TURNED_OFF.includes(key));
    confirmOpen.value = false;
    confirmKey.value = null;
}

async function saveSetting(key, value) {
    if (savingKey.value) return;
    savingKey.value = key;
    try {
        const response = await send('put', 'admin/renewals/settings', {[key]: value});
        applySettings(response.data.data, {keepEmailEdits: true});
        notify('Saved.');
    } catch (e) {
        notify(e?.response?.data?.message || 'Could not save.', 'error');
        // Never leave a switch showing a state the server did not accept.
        syncSwitches();
    } finally {
        savingKey.value = null;
    }
}

async function saveEmails() {
    if (savingKey.value) return;
    savingKey.value = 'billing_notification_emails';
    try {
        const response = await send('put', 'admin/renewals/settings', {
            billing_notification_emails: emails.value,
        });
        applySettings(response.data.data);
        notify('Recipients saved.');
    } catch (e) {
        notify(e?.response?.data?.message || 'Could not save the recipients.', 'error');
    } finally {
        savingKey.value = null;
    }
}

// Resetting the page fires the page watcher, so these only reload directly when
// the page was already 1 - otherwise every filter change costs two paginated
// queries and the first is thrown away.
watch(period, () => {
    if (page.value !== 1) {
        page.value = 1;
    }
    loadAll();
});
watch([filterStatus, filterFailureCode, filterPortal], () => {
    if (page.value !== 1) {
        page.value = 1;

        return;
    }
    reloadAttempts();
});
// loadAll sets `loading` synchronously and reloads the attempts itself, so a
// page reset it triggered must not queue a second query behind it.
watch(page, () => {
    if (!loading.value) reloadAttempts();
});

onMounted(loadAll);
</script>

<style scoped>
.op-half {
    opacity: 0.5;
    transition: opacity 0.15s;
}
</style>
